import { Skill } from "../types/prisma-enums";
import type { GameContent, Item, ItemVisual, Level } from "./types";

/**
 * Sky Quest — Numbers · games-spec.md §4.
 *
 * ─── 🔴 THIS TABLE IS THE PRESCHOOL NUMERACY QUESTION BANK, L1–L3 ───────────
 *
 * Every item below comes from `Preschool_Numeracy_Question_Bank_L1-L3.md`, and
 * the bank's own IDs are kept in the comments so a row here can be traced to the
 * row it was authored from. Its design rules are the rules this file obeys:
 *
 *   1. **Three options only** — which is what the three lanes can offer.
 *   2. **Distractors are near-misses, not random** — usually ±1, so a child who
 *      miscounts by one lands somewhere that means something on a report.
 *   3. **Never three numerals with no picture at Level 1** — every counting item
 *      carries `visual`, and `CountScene` draws it.
 *   4. **One idea per question.**
 *   5. **Prompts under twelve spoken words.**
 *   6. **Positive framing** — no "which is wrong".
 *
 * ⚠️ WHAT IS DELIBERATELY ABSENT, AND WHY IT IS NOT AN OVERSIGHT. The bank has
 * seven strands this game cannot ask: **1D size**, **1E shapes**, **1F sorting**,
 * **2F patterns**, **2G position**, **3F ordinal**, **3H patterns**. Their
 * answers are not numbers — *"the big elephant"*, *"a triangle"*, *"blue"* — and
 * `validatePack`'s closed domain list exists precisely to keep this game
 * numerical: *"Four other games own colours, shapes, letters, phonics and
 * feelings. This one owns number."* Bridge Patterns owns 2F/3H, Turo's World
 * owns 1D/1E. Putting them here would stamp NUMERACY on a report card for
 * something that is not numeracy, and nothing downstream could tell.
 *
 * 🔴 THE BANK'S OWN IMPLEMENTATION NOTE IS ALREADY SATISFIED. It asks:
 * *"Randomise option order at runtime so children cannot learn a positional
 * habit."* `shuffleChoices` in progress.ts does exactly that on every sampled
 * item, which is why the orders written below are the bank's and do not matter.
 *
 * ─── 🔴 THE ARTWORK IS NAMED, NOT DESCRIBED ─────────────────────────────────
 *
 * `asset` is a sprite id under `public/shared/sprites/count/`, cut from the two
 * reference sheets. `visual.parts` says how many of it to draw and in what
 * groups. Together they are the bank's *"On-screen visual"* column, as data.
 *
 * ⚠️ THE MALAYALAM COLUMN OF THIS TABLE IS GONE (ADR-044). Every prompt below
 * is the English the bank was authored in.
 */

/** Terse constructor: these tables are read far more often than written. */
const item = (
  id: string,
  en: string,
  audioKey: string,
  target: string | number,
  choices: (string | number)[],
  asset?: string,
): Item => ({ id, prompt: { en }, audioKey, target, choices, asset });

/**
 * The same item, but answered by SELECTING rather than by building — `respond:
 * 'choose'` (see `./types`).
 *
 * 🔴 EVERY ITEM IN THIS GAME IS ONE. The bank has no give-N rows: every question
 * offers exactly three options and the child picks one, which on the camera path
 * means flying through one of three rings. `motorAction` still earns the physical
 * indicators on the report card (ADR-031 Amendment 1); it no longer decides how
 * an item is answered, which is what `respondFor` exists to keep straight.
 */
const choose = (...args: Parameters<typeof item>): Item => ({
  ...item(...args),
  respond: "choose",
});

/**
 * The same item, with a countable scene — the bank's *"On-screen visual"*.
 *
 * 🔴 A WRAPPER RATHER THAN AN EIGHTH POSITIONAL ARGUMENT, exactly as `choose`
 * is. `item()` already takes seven; a `{ parts, op }` object squeezed in as an
 * eighth would push the readable part of every row off the end of the line.
 */
const seen = (visual: ItemVisual, it: Item): Item => ({ ...it, visual });

/** Shorthands, so a row reads as the question rather than as an object literal. */
const count = (n: number): ItemVisual => ({ parts: [n] });
const plus = (a: number, b: number): ItemVisual => ({
  parts: [a, b],
  op: "add",
});
const minus = (a: number, b: number): ItemVisual => ({
  parts: [a, b],
  op: "take",
});

/* ── L1 · bank Level 1 — counting to 5, numerals 1–5, more & fewer ───────────
 *
 * 🔴 POINT ONLY, NO COUNTING DEMAND — games-spec.md §4, and `motorAction` is
 * null. This is the level a 3-year-old meets the game on; giving it a jump
 * "because the other levels have one" would put a physical demand on the first
 * thirty seconds of a child's first game.
 */
const L1: Level = {
  level: 1,
  name: { en: "How many?" },
  /*
   * ⚠️ SHORT ENOUGH FOR THE SCREEN IT IS PRINTED ON. `width.test.ts` measures
   * every level name and objective against 1366×768 — the laptop this product
   * targets — and a three-clause objective overflowed it. The
   * strand list belongs in the comments above, not on a teacher's screen.
   */
  objective: {
    en: "Count to 5 and know the numerals",
  },
  ncf: ["C-8.3"],
  itemCount: 6,
  motorAction: null,
  items: [
    /* ── 1A · subitizing & counting to 5 ──
     * The objects are drawn and the options are numerals: count what you see,
     * then find the number that says how many. Bank design rule 3 is why the
     * objects are there at all. */
    seen(
      count(2),
      choose(
        "sq.l1.a01",
        "How many butterflies do you see?",
        "sq_l1_a01",
        2,
        [1, 2, 3],
        "butterfly",
      ),
    ),
    seen(
      count(3),
      choose(
        "sq.l1.a02",
        "Count the stars with me.",
        "sq_l1_a02",
        3,
        [2, 3, 4],
        "star",
      ),
    ),
    seen(
      count(1),
      choose("sq.l1.a03", "How many fish?", "sq_l1_a03", 1, [1, 2, 3], "fish"),
    ),
    seen(
      count(4),
      choose(
        "sq.l1.a04",
        "How many balls are there?",
        "sq_l1_a04",
        4,
        [3, 4, 5],
        "ball",
      ),
    ),
    seen(
      count(5),
      choose(
        "sq.l1.a05",
        "Count the flowers.",
        "sq_l1_a05",
        5,
        [3, 4, 5],
        "flower",
      ),
    ),
    seen(
      count(3),
      choose(
        "sq.l1.a06",
        "How many ducks?",
        "sq_l1_a06",
        3,
        [3, 4, 5],
        "duck",
      ),
    ),
    seen(
      count(2),
      choose("sq.l1.a07", "How many shoes?", "sq_l1_a07", 2, [1, 2, 3], "shoe"),
    ),
    /*
     * ⚠️ THE BANK'S L1-A-07 IS NOT HERE — it is *"(audio only) three claps play:
     * how many claps did you hear?"*, and this game has no clap track. An item
     * whose whole stimulus is a recording nobody has made would be silent on
     * screen and unanswerable, which is worse than absent. It belongs in the
     * recording script first.
     */

    /* ── 1B · numeral recognition 1–5 ──
     * 🔴 NO COUNTABLE SCENE ON MOST OF THESE, AND THAT IS THE STRAND. These ask
     * about the SYMBOL — *"which one is the number three?"* — so a picture to
     * count would answer the question for the child. Bank rule 3 forbids bare
     * numerals for COUNTING questions; this strand is the other thing.
     *
     * 🔴 ASK FOR THE THING, NOT FOR THE WORD FOR THE THING — THE FOUNDER'S, AND IT
     * IS A RULE RATHER THAN ONE EDIT. b04 read *"Which number tells how many
     * apples?"*: six words, two of them about NUMBERS AS OBJECTS, put to a
     * three-year-old who is being asked to do one thing — count four apples.
     * *"How many apples?"* asks for exactly the same answer in three words that
     * a pre-reader has already heard a hundred times.
     *
     * ⚠️ THE ITEM DID NOT CHANGE, ONLY THE SENTENCE. `choiceModeFor` reads the
     * SCENE, never the prompt — four apples are drawn, so the three rings still
     * carry numerals and the child still answers by picking the symbol 4. The
     * bridge from quantity to numeral is intact; what went is the narration of
     * it.
     *
     * ⚠️ WHICH LEAVES b04 PHRASED EXACTLY LIKE A 1A COUNTING ITEM, AND THAT IS THE
     * PRICE. Read the strand by its prompts and this row now looks like it
     * belongs a few lines up. It stays here because the strand is defined by
     * what the child must produce — a numeral — and that is unchanged. The four
     * rows around it have no scene and cannot take this treatment: *"which one
     * is the number three?"* has no "how many" form, because there is nothing
     * on screen to count. */
    choose(
      "sq.l1.b01",
      "Which one is the number three?",
      "sq_l1_b01",
      3,
      [1, 3, 5],
    ),
    choose("sq.l1.b02", "Find the number one.", "sq_l1_b02", 1, [1, 2, 4]),
    choose("sq.l1.b03", "Which number is five?", "sq_l1_b03", 5, [2, 5, 3]),
    seen(
      count(4),
      choose(
        "sq.l1.b04",
        "How many apples?",
        "sq_l1_b04",
        4,
        [3, 4, 5],
        "apple",
      ),
    ),
    choose("sq.l1.b05", "Show me the number two.", "sq_l1_b05", 2, [5, 2, 1]),

    /*
     * 🔴 THE "more / fewer" STRAND IS GONE — THE FOUNDER'S, AND IT IS A DELETION
     * RATHER THAN A REWORDING. Four items asked *"which side has more cookies?"*,
     * *"which side has fewer eggs?"* and two more like them. They are out of Sky
     * Quest entirely.
     *
     * ⚠️ WHAT WENT WITH THEM, SO NOBODY RE-ADDS THEM BY ACCIDENT. They were the
     * bank's 1C, and the note that used to stand here explained a compromise:
     * the bank's third option is *"they are the same"*, this game's answers are
     * numbers, so the child picked the COUNT of the fuller group and the third
     * lane carried a near-miss. That compromise no longer needs making.
     *
     * ⚠️ THE ENGINE KEEPS ITS COMPARISON SUPPORT, AND THAT IS NOT DEAD CODE BY
     * OVERSIGHT. `ItemVisual.op` still admits `'vs'`, `CountScene` still draws
     * the chalk rod between two groups, `adaptive.ts` still scores a comparison,
     * and `validatePack` still admits the `compare` domain — because the JSON
     * pack's own L1 still carries nine *"fly to the cloud with the most birds"*
     * items. Ripping the capability out here would take the pack's content with
     * it, silently, the day it is wired in.
     */
  ],
};

/* ── L2 · bank Level 2 — counting to 10, one more / one less, ± within 5 ───── */
const L2: Level = {
  level: 2,
  name: { en: "Count to ten" },
  objective: {
    en: "Count to 10, and add or take away within 5",
  },
  ncf: ["C-8.3", "C-8.5"],
  itemCount: 6,
  motorAction: { kind: "jump", countsTo: 5 },
  items: [
    /* ── 2A · counting to 10 ── */
    seen(
      count(7),
      choose(
        "sq.l2.a01",
        "How many balloons?",
        "sq_l2_a01",
        7,
        [6, 7, 8],
        "balloon",
      ),
    ),
    seen(
      count(9),
      choose(
        "sq.l2.a02",
        "Count the birds.",
        "sq_l2_a02",
        9,
        [8, 9, 10],
        "bird",
      ),
    ),
    seen(
      count(6),
      choose(
        "sq.l2.a03",
        "How many cookies?",
        "sq_l2_a03",
        6,
        [5, 6, 7],
        "cookie",
      ),
    ),
    seen(
      count(10),
      choose(
        "sq.l2.a04",
        "How many blocks?",
        "sq_l2_a04",
        10,
        [8, 9, 10],
        "block",
      ),
    ),
    seen(
      count(8),
      choose(
        "sq.l2.a05",
        "How many candles?",
        "sq_l2_a05",
        8,
        [7, 8, 9],
        "candle",
      ),
    ),

    /* ── 2B · numeral recognition & number order 1–10 ──
     * The symbol strand again: no scene, because the numeral IS the question. */
    choose(
      "sq.l2.b01",
      "Which one is the number seven?",
      "sq_l2_b01",
      7,
      [1, 7, 9],
    ),
    choose(
      "sq.l2.b02",
      "Which one is the number ten?",
      "sq_l2_b02",
      10,
      [7, 10, 1],
    ),
    choose(
      "sq.l2.b03",
      "One, two, three. What comes next?",
      "sq_l2_b03",
      4,
      [4, 5, 2],
    ),
    choose("sq.l2.b04", "What comes after seven?", "sq_l2_b04", 8, [8, 9, 6]),
    choose(
      "sq.l2.b05",
      "What number comes before four?",
      "sq_l2_b05",
      3,
      [3, 5, 2],
    ),
    choose(
      "sq.l2.b06",
      "Which number is the biggest?",
      "sq_l2_b06",
      8,
      [2, 8, 5],
    ),

    /* ── 2C · one more / one less ── */
    seen(
      plus(3, 1),
      choose(
        "sq.l2.c01",
        "Three frogs, and one more hops in. Now how many?",
        "sq_l2_c01",
        4,
        [3, 4, 5],
        "frog",
      ),
    ),
    seen(
      minus(5, 1),
      choose(
        "sq.l2.c02",
        "Five sweets. One is eaten. How many are left?",
        "sq_l2_c02",
        4,
        [3, 4, 5],
        "sweet",
      ),
    ),
    /*
     * ⚠️ THE BANK SHOWS A NUMERAL HERE AND WE SHOW OBJECTS, WHICH IS A CHANGE
     * MADE ON PURPOSE. Its L2-C-03 is *"Numeral 6 shown → what is one more than
     * six?"*, and this game has no way to draw a lone numeral in the scene. The
     * alternative was to draw nothing at all — and *"what is one more than six?"*
     * over an empty sky is a memory task, not a numeracy one, for a four-year-old
     * who has just been asked to fly. Six counters is the same question with
     * something to think WITH, which is what bank rule 3 is about.
     */
    seen(
      count(6),
      choose(
        "sq.l2.c03",
        "What is one more than six?",
        "sq_l2_c03",
        7,
        [5, 6, 7],
        "counter-red",
      ),
    ),
    seen(
      count(5),
      choose(
        "sq.l2.c04",
        "What is one less than five?",
        "sq_l2_c04",
        4,
        [4, 5, 6],
        "counter-blue",
      ),
    ),
    seen(
      plus(4, 1),
      choose(
        "sq.l2.c05",
        "Four ducks. One more arrives. How many now?",
        "sq_l2_c05",
        5,
        [4, 5, 6],
        "duck",
      ),
    ),

    /* ── 2D · addition within 5 ──
     * 🔴 EVERY ONE CARRIES ITS QUANTITIES. `validatePack`'s `VISUAL_REQUIRED`
     * makes this mandatory for arithmetic domains, and the reason is that a bare
     * sum put to a pre-reader is not a hard question but an impossible one. */
    seen(
      plus(2, 1),
      choose(
        "sq.l2.d01",
        "Two apples and one more. How many altogether?",
        "sq_l2_d01",
        3,
        [2, 3, 4],
        "apple",
      ),
    ),
    seen(
      plus(1, 1),
      choose(
        "sq.l2.d02",
        "One and one makes how many?",
        "sq_l2_d02",
        2,
        [1, 2, 3],
        "star",
      ),
    ),
    seen(
      plus(3, 2),
      choose(
        "sq.l2.d03",
        "Three fish and two fish. How many in all?",
        "sq_l2_d03",
        5,
        [4, 5, 6],
        "fish",
      ),
    ),
    seen(
      plus(2, 2),
      choose(
        "sq.l2.d04",
        "Two cars and two cars. How many?",
        "sq_l2_d04",
        4,
        [3, 4, 5],
        "car",
      ),
    ),
    seen(
      plus(1, 3),
      choose(
        "sq.l2.d05",
        "One ball, and three more roll in. How many?",
        "sq_l2_d05",
        4,
        [3, 4, 5],
        "ball",
      ),
    ),

    /* ── 2E · subtraction within 5 ── */
    seen(
      minus(5, 2),
      choose(
        "sq.l2.e01",
        "Five birds. Two fly away. How many are left?",
        "sq_l2_e01",
        3,
        [2, 3, 4],
        "bird",
      ),
    ),
    seen(
      minus(4, 1),
      choose(
        "sq.l2.e02",
        "Four leaves. One falls. How many are left?",
        "sq_l2_e02",
        3,
        [2, 3, 4],
        "leaf",
      ),
    ),
    seen(
      minus(3, 2),
      choose(
        "sq.l2.e03",
        "Three balloons. Two float off. How many are left?",
        "sq_l2_e03",
        1,
        [1, 2, 3],
        "balloon",
      ),
    ),
    seen(
      minus(5, 3),
      choose(
        "sq.l2.e04",
        "Five balloons. Three pop. How many are left?",
        "sq_l2_e04",
        2,
        [1, 2, 3],
        "balloon",
      ),
    ),

    /* ── 2H · counting down from 10 (2026-10-16) ──
     *
     * 🔴 NOT FROM THE BANK, WHICH IS WHY THE LETTER IS H. The bank's Level 2
     * runs A–G (2F patterns and 2G position are deliberately absent, see the
     * header), so H cannot collide with a bank row or with an old event's
     * `itemId`. Added on the reviewer's ask for later levels to bring something
     * new rather than more of the same: L2 counts UP in 2B ("one, two, three —
     * what comes next?"); this counts DOWN, the other half of number order.
     *
     * ⚠️ IT IS NOT "WORKING BACKWARDS" IN THE SENSE L3's REMOVED STRANDS WERE.
     * Those handed a child an answer and asked for the missing part of the
     * question. This hands over a run of numbers and asks for the next one —
     * the same forward demand as 2B, read in the other direction.
     *
     * The symbol strand again: no scene, because a row of objects would turn
     * "what comes next?" into "how many?". No zero: L2 never asks for one, and
     * the one item that teaches it lives in L3 (`sq.l3.c06`). */
    choose(
      "sq.l2.h01",
      "Count down: five, four, three. What comes next?",
      "sq_l2_h01",
      2,
      [2, 1, 4],
    ),
    choose(
      "sq.l2.h02",
      "Count down: ten, nine, eight. What comes next?",
      "sq_l2_h02",
      7,
      [7, 6, 9],
    ),
    choose(
      "sq.l2.h03",
      "Count down: seven, six, five. What comes next?",
      "sq_l2_h03",
      4,
      [4, 3, 6],
    ),
    choose(
      "sq.l2.h04",
      "Count down: four, three, two. What comes next?",
      "sq_l2_h04",
      1,
      [1, 2, 3],
    ),
    choose(
      "sq.l2.h05",
      "Count down: nine, eight, seven. What comes next?",
      "sq_l2_h05",
      6,
      [6, 5, 8],
    ),
  ],
};

/* ── L3 · bank Level 3 — to 10, ± within 5, bonds, missing number, sharing ── */
const L3: Level = {
  level: 3,
  name: { en: "Add and take away" },
  /*
   * 🔴 IT NAMES THE MOVEMENT, AND `pacing.test.ts` HOLDS THAT. An objective is
   * teacher-facing — the one line an adult reads to know what the level asks a
   * child to *do* — so a level with a jump has to say jump, or the seated
   * fallback has nothing to be a fallback FROM in the eyes of the person
   * deciding whether a child can play it.
   */
  objective: {
    en: "Jump to add and take away with numbers to 5",
  },
  ncf: ["C-8.3", "C-8.5"],
  itemCount: 6,
  motorAction: { kind: "jump", countsTo: 10 },
  items: [
    /* ── 3A · counting & numerals to 10 ── */
    /*
     * 🔴 TO TEN, NOT TO TWENTY — THE CEILING IS THE AGE GROUP, NOT THE LEVEL.
     * These items asked for 12, 15, 17 and 20, which made L3 the only place in
     * the product where a child met a two-digit number. This is a PRESCHOOL
     * product, and the founder's instruction is that every question stays within
     * ten whatever the level.
     *
     * 🔴 AND THE ARITHMETIC STAYS WITHIN FIVE — A SECOND, NARROWER CEILING, AND
     * IT IS THE FOUNDER'S. *"in level 3 addition/subtraction just have to
     * between 1 to 5"*, answers included. So every add and every take-away below
     * has both parts and its answer inside 1–5; only the zero item ends outside
     * it, downwards, and it is defended where it stands.
     *
     * ⚠️ WHICH MEANS L3 IS NO LONGER HARDER THAN L2 *AT ARITHMETIC*, AND THAT IS
     * THE DECISION, NOT AN OVERSIGHT. L2 adds and takes away within five too.
     * What is left to separate them is real and is what this level now rests on:
     * counting and numerals to TEN where L2 stops at ten only for counting,
     * number bonds, missing-number, halving — the ideas L2 never asks for at all.
     * A reviewer who reads this level as a duplicate of L2 has read only its
     * sums.
     */
    seen(
      count(8),
      choose("sq.l3.a01", "How many stars?", "sq_l3_a01", 8, [7, 8, 9], "star"),
    ),
    seen(
      count(10),
      choose(
        "sq.l3.a02",
        "Count the shells.",
        "sq_l3_a02",
        10,
        [8, 9, 10],
        "shell",
      ),
    ),
    choose(
      "sq.l3.a03",
      "What number comes after eight?",
      "sq_l3_a03",
      9,
      [7, 9, 10],
    ),
    choose("sq.l3.a04", "Which one is seven?", "sq_l3_a04", 7, [5, 7, 9]),
    seen(
      plus(3, 2),
      choose(
        "sq.l3.a05",
        "You had three apples. I gave you two more. How many?",
        "sq_l3_a05",
        5,
        [3, 4, 5],
        "apple",
      ),
    ),

    /*
     * ─── 🔴 EVERY SUM IN L3 IS A STORY ABOUT A THING, NOT A SUM ───────────
     *
     * The founder, on reading *"Five take away three. How many are left?"*:
     * *"no that is not the way you should ask — you had 5 colour, I took 3, how
     * many left"*. Every arithmetic prompt below is now that shape:
     *
     *     someone HAS some things  →  something HAPPENS  →  how many now
     *
     * ⚠️ AND "take away" IS GONE FROM THE GAME. It is the name of an
     * operation, not of anything that happens to a child — nobody has ever taken
     * three away from five in a nursery. *"I took three"* is the same arithmetic
     * described as an event, and an event is a thing a four-year-old can picture
     * while they count. `plus`, `minus`, `makes` and `altogether` went the same
     * way wherever a verb would do.
     *
     * 🔴 AND EVERY ONE NAMES A DRAWN OBJECT, WHICH IS WHY THE COUNTERS ARE
     * GONE. Eleven of these carried `counter-red`/`-blue`/`-green` — coloured
     * discs, drawn because the question named nothing to draw. A story needs a
     * noun, so the discs are replaced by things the sprite sheet already has:
     * balls, blocks, shells, flowers, stars, cookies, apples. `skyQuestPrompts.test.ts`
     * holds the pairing, so a prompt can never name one thing while the scene
     * draws another.
     *
     * ⚠️ "you" AND "I", AND THAT IS THE FOUNDER'S WORDING RATHER THAN A STYLE
     * CHOICE. *"Five mangoes. Two are eaten."* is a passive sentence with nobody
     * in it. *"You had five mangoes. I ate two."* has two people, and the child
     * is one of them — which is what makes it a story instead of a sum with
     * decoration.
     */

    /* ── 3B · addition within 5 — both parts AND the answer ──
     * 🔴 EVERY ONE CARRIES ITS QUANTITIES, exactly as L2's do. A bare sum put to
     * a pre-reader is not a hard question but an impossible one, which is why
     * `validatePack`'s `VISUAL_REQUIRED` makes the scene mandatory for
     * arithmetic and why `plus()` is on every row below. */
    seen(
      plus(4, 1),
      choose(
        "sq.l3.b01",
        "You had four balls. I gave you one more. How many?",
        "sq_l3_b01",
        5,
        [3, 4, 5],
        "ball",
      ),
    ),
    seen(
      plus(1, 2),
      choose(
        "sq.l3.b02",
        "You had one block. I gave you two more. How many?",
        "sq_l3_b02",
        3,
        [2, 3, 4],
        "block",
      ),
    ),
    seen(
      plus(2, 3),
      choose(
        "sq.l3.b03",
        "You had two shells. I gave you three more. How many?",
        "sq_l3_b03",
        5,
        [3, 4, 5],
        "shell",
      ),
    ),
    seen(
      plus(3, 1),
      choose(
        "sq.l3.b04",
        "You had three flowers. I gave you one more. How many?",
        "sq_l3_b04",
        4,
        [3, 4, 5],
        "flower",
      ),
    ),
    seen(
      plus(2, 2),
      choose(
        "sq.l3.b05",
        "You had two stars. I gave you two more. How many?",
        "sq_l3_b05",
        4,
        [3, 4, 5],
        "star",
      ),
    ),

    /* ── 3C · subtraction within 5 ──
     * The number taken away is never larger than the number there was, and
     * neither is ever above five. */
    seen(
      minus(5, 3),
      choose(
        "sq.l3.c01",
        "You had five cookies. I took three. How many are left?",
        "sq_l3_c01",
        2,
        [1, 2, 3],
        "cookie",
      ),
    ),
    seen(
      minus(5, 4),
      choose(
        "sq.l3.c02",
        "You had five blocks. I took four. How many are left?",
        "sq_l3_c02",
        1,
        [1, 2, 3],
        "block",
      ),
    ),
    seen(
      minus(5, 1),
      choose(
        "sq.l3.c03",
        "You had five fish. One swam away. How many are left?",
        "sq_l3_c03",
        4,
        [3, 4, 5],
        "fish",
      ),
    ),
    seen(
      minus(4, 3),
      choose(
        "sq.l3.c04",
        "You had four leaves. Three blew away. How many are left?",
        "sq_l3_c04",
        1,
        [1, 2, 3],
        "leaf",
      ),
    ),
    seen(
      minus(5, 2),
      choose(
        "sq.l3.c05",
        "You had five mangoes. I ate two. How many are left?",
        "sq_l3_c05",
        3,
        [2, 3, 4],
        "mango",
      ),
    ),
    /*
     * 🔴 THE ZERO ITEM IS KEPT, AND IT IS THE ONE ROW THAT NEEDS DEFENDING.
     * *"Five take away five"* is the bank's L3-C-06 shrunk to the new ceiling,
     * and its answer is `0` — a numeral this game has never drawn and a lane a
     * child has never had to fly through. That is the point: nothing is a
     * number, and a five-year-old who has only ever been asked for one-or-more
     * has not met it.
     *
     * ⚠️ AND IT IS THE ONE ANSWER OUTSIDE 1–5, WHICH IS NOT A BREACH OF THE RULE
     * ABOVE. Both numbers the question STATES are five; the ceiling is a ceiling,
     * and zero is under it. Rewriting this row to keep its answer inside the
     * range would mean deleting the only place in the product where zero is
     * taught.
     */
    seen(
      minus(5, 5),
      choose(
        "sq.l3.c06",
        "You had five sweets. I ate all five. How many are left?",
        "sq_l3_c06",
        0,
        [0, 1, 5],
        "sweet",
      ),
    ),

    /*
     * 🔴 3D, 3E AND 3G ARE GONE — NINE ITEMS, ON THE FOUNDER'S INSTRUCTION.
     * The bank's number bonds, its missing-number rows and its sharing pair all
     * left together, and they left because they are the same demand wearing
     * three hats:
     *
     *   · **bonds** — *"you have three shells, I want five, how many more?"*
     *   · **missing number** — *"you have three balls, how many more make five?"*
     *   · **halving** — *"share four cookies with me, how many each?"*
     *
     * ⚠️ EVERY ONE OF THEM ASKS A CHILD TO WORK BACKWARDS, WHICH IS A
     * DIFFERENT SKILL FROM THE ONE THIS LEVEL TEACHES. `3B` and `3C` hand a child
     * a quantity, do something to it, and ask what is there now — count what you
     * see, then count what happened. These nine handed over an ANSWER and asked
     * for the missing part of the question. That is algebra with the numbers
     * filed off, and a four-year-old who can add three and two perfectly well
     * fails it for a reason that has nothing to do with number.
     *
     * ⚠️ AND IT LEFT L3 SAYING ONE THING. Sixteen items: count what you
     * see, know the numeral, and add or take away a few things in a story.
     * (3I, below, adds counting by twos on purpose — 2026-10-16.) A
     * level that asks four kinds of question measures four things and reports
     * one, which is how a report card ends up saying "numeracy" about a child
     * who was actually beaten by the wording.
     *
     * 🔴 `C-8.6` WENT FROM `ncf` WITH THEM. It was the code those strands
     * were authored against; leaving it on the level would claim a curriculum
     * outcome nothing on the level still teaches, and that claim reaches a
     * teacher's screen.
     */

    /* ── 3I · counting by twos (2026-10-16) ──
     *
     * 🔴 NOT FROM THE BANK, WHICH IS WHY THE LETTER IS I. The bank's Level 3
     * runs A–H, and D, E and G were retired above, so a fresh letter keeps old
     * `sq.l3.d…` answers from being read as this strand on a teacher's screen.
     * Added on the reviewer's ask for an L3 question a child has not met in L1
     * or L2 — every step so far has been one.
     *
     * ⚠️ FORWARD, LIKE 2B AND 2H — NOT ONE OF THE RETIRED BACKWARDS STRANDS. The
     * child continues a run of numbers; nothing asks for the missing part of a
     * sum.
     *
     * ⚠️ NO SCENE, AND THAT IS THE ARITHMETIC CEILING SPEAKING. `CountScene` draws
     * groups with a `+` between them, so pairs of shoes would be read as
     * "2 + 2 + 2 + 2" — a sum to eight, over the founder's within-5 line for
     * adding. As numerals it is number order, and every number stays within ten,
     * distractors included: one more (counting by ones) or one step too far. */
    choose(
      "sq.l3.i01",
      "Count by twos: two, four, six. What comes next?",
      "sq_l3_i01",
      8,
      [7, 8, 10],
    ),
    choose(
      "sq.l3.i02",
      "Count by twos: two, four. What comes next?",
      "sq_l3_i02",
      6,
      [5, 6, 8],
    ),
    choose(
      "sq.l3.i03",
      "Count by twos: four, six, eight. What comes next?",
      "sq_l3_i03",
      10,
      [8, 9, 10],
    ),
    choose(
      "sq.l3.i04",
      "Count by twos: one, three, five. What comes next?",
      "sq_l3_i04",
      7,
      [6, 7, 9],
    ),
    choose(
      "sq.l3.i05",
      "Count by twos: three, five, seven. What comes next?",
      "sq_l3_i05",
      9,
      [8, 9, 10],
    ),
  ],
};

export const SKY_QUEST: GameContent = {
  id: "sky-quest",
  category: "NUMBERS",
  contentSkill: Skill.NUMERACY,
  /**
   * 🔴 BOTH motor skills, emitted on every round. This is how NCF-FS **C-3
   * physical development** reaches a report card when no content category is
   * named "physical" (ADR-031, Amendment 1).
   */
  motorSkills: [Skill.COORDINATION, Skill.BODY_AWARENESS],
  emoji: "🔢",
  exertion: "high",
  levels: [L1, L2, L3],
};
